<template>
  <div class="lg:!mx-0 flex">
    <div class="flex flex-col max-h-content px-6 pb-2.5 min-h-content space-y-2.5 w-screen">
      <!-- Header Section -->
      <div class="flex items-stretch justify-between mt-2.5 element-island">
        <div class="flex items-center space-x-3">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('LineChart.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('LineChart.subTitle') }}
            </p>
          </div>
        </div>
      </div>

      <template v-if="wbsCodes.length">
        <!-- Main Content Area -->
        <div class="flex flex-col border border-gray-300 p-5 space-y-6 element-island w-full overflow-y-auto flex-1">
          <!-- Input and Summary Section -->
          <div class="flex w-full space-x-6">
            <!-- Left Column - Input/Filters -->
            <div class="w-1/3 space-y-4">
              <div class="p-4 space-y-4">
                <label class="block">
                  <span class="text-sm font-medium text-gray-700">{{ $t('LineChart.label.wbsCode') }}</span>

                  <AppSelect
                    v-model.number="forecastForm.form.wbs_code_id"
                    :options="wbsCodes.map((code) => ({ value: code.id, label: code.code }))"
                    name="wbs_code"
                    data-test="wbs_code"
                    class="form-input mt-1"
                  />
                </label>

                <label class="block">
                  <span class="text-sm font-medium text-gray-700">{{ $t('LineChart.label.wbsDescription') }}</span>

                  <AppSelect
                    v-model.number="forecastForm.form.wbs_code_id"
                    :options="wbsCodes.map((code) => ({ value: code.id, label: code.description }))"
                    name="wbs_code_description"
                    data-test="wbs_code_description"
                    class="form-input mt-1"
                  />
                </label>

                <div class="grid grid-cols-2 gap-4">
                  <label class="block">
                    <span class="text-sm font-medium text-gray-700">{{ $t('LineChart.label.startDate') }}</span>

                    <AppInput
                      v-model="forecastForm.form.start"
                      class="form-input"
                      type="date"
                      name="start"
                      :error="forecastForm.getErrors('start')"
                    />
                  </label>

                  <label class="block">
                    <span class="text-sm font-medium text-gray-700">{{ $t('LineChart.label.endDate') }}</span>

                    <AppInput
                      v-model="forecastForm.form.end"
                      class="form-input"
                      type="date"
                      name="end"
                      :error="forecastForm.getErrors('end')"
                    />
                  </label>
                </div>

                <button
                  type="button"
                  class="w-full btn btn--primary-blue flex items-center justify-center space-x-2"
                  :disabled="fetchForecastAction.state.value === States.LOADING"
                  @click="fetchForecast"
                >
                  <svg
    fill="none"
    viewBox="0 0 24 24"
 v-if="fetchForecastAction.state.value === States.LOADING"
                   
                    class="animate-spin h-5 w-5 text-white">
    <circle
        class="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="4"
    />

    <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
</svg>

                  <span>
                    {{ $t('LineChart.button.loadRates') }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Right Column - Summary Metrics -->
            <div
              v-if="fetchForecastAction.isNot(States.LOADING)"
              class="w-2/3 p-6 flex flex-col"
            >
              <!-- Financial Metrics Grid -->
              <div class="grid grid-cols-2 divide-x divide-gray-200 mb-6">
                <div class="space-y-3 px-3">
                  <div class="flex justify-between items-center">
                    <span class="font-medium text-gray-600">{{ $t('LineChart.metrics.tenderAmount') }}</span>

                    <span class="text-lg font-semibold text-gray-900">{{ $tcc(wbsCode?.tender_cost) }}</span>
                  </div>

                  <div class="flex justify-between items-center">
                    <span class="font-medium text-gray-600">{{ $t('LineChart.metrics.forecastCost') }}</span>

                    <span class="text-lg font-semibold text-gray-900">{{ $tcc(tender.cost_forecast) }}</span>
                  </div>

                  <div class="flex justify-between items-center">
                    <span class="font-medium text-gray-600">{{ $t('LineChart.metrics.profitLoss') }}</span>

                    <span
                      :class="[
                        'text-lg font-semibold',
                        tender.profit >= 0 ? 'text-green-600' : 'text-red-600'
                      ]"
                    >
                      {{ $tcc(tender.profit) }}
                    </span>
                  </div>
                </div>

                <div class="space-y-3 px-3">
                  <div class="flex justify-between items-center">
                    <span class="font-medium text-gray-600">{{ $t('LineChart.metrics.costToDate') }}</span>

                    <span class="text-lg font-semibold text-gray-900">{{ $tcc(tender.cost_to_date) }}</span>
                  </div>

                  <div class="flex justify-between items-center">
                    <span class="font-medium text-gray-600">{{ $t('LineChart.metrics.earnedAmount') }}</span>

                    <span class="text-lg font-semibold text-gray-900">{{ $tcc(tender.earned_amount) }}</span>
                  </div>

                  <div class="flex justify-between items-center">
                    <span class="font-medium text-gray-600">{{ $t('LineChart.metrics.costToComplete') }}</span>

                    <span class="text-lg font-semibold text-gray-900">{{ $tcc(tender.cost_to_complete) }}</span>
                  </div>
                </div>
              </div>

              <!-- Rate Indicators -->
              <div
                v-if="wbsCode"
                class="flex space-x-4 justify-center mt-auto"
              >
                <div class="bg-red-50 border-2 border-red-200 rounded-lg px-4 py-3 text-center min-w-[120px]">
                  <p class="text-sm font-medium text-red-700 mb-1">
                    {{ $t('LineChart.rateIndicators.tenderRate') }}
                  </p>

                  <p class="text-lg font-bold text-red-800">
                    {{ $tcc(wbsCode.unitCost) }}/{{ wbsCode.unit }}
                  </p>
                </div>

                <div class="bg-orange-50 border-2 border-orange-200 rounded-lg px-4 py-3 text-center min-w-[120px]">
                  <p class="text-sm font-medium text-orange-700 mb-1">
                    {{ $t('LineChart.rateIndicators.toDate') }}
                  </p>

                  <p class="text-lg font-bold text-orange-800">
                    {{ $tcc(tender.to_date_unit_cost) }}/{{ wbsCode.unit }}
                  </p>
                </div>

                <div class="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3 text-center min-w-[120px]">
                  <p class="text-sm font-medium text-green-700 mb-1">
                    {{ $t('LineChart.rateIndicators.toComplete') }}
                  </p>

                  <p class="text-lg font-bold text-green-800">
                    {{ $tcc(tender.to_complete_unit_cost) }}/{{ wbsCode.unit }}
                  </p>
                </div>
              </div>
            </div>

            <div
              v-else
              class="flex justify-center items-center h-64 mx-auto w-2/3"
            >
              <AppSpinner class="w-12 h-12" />
            </div>
          </div>

          <!-- Chart Section -->
          <div
            v-if="wbsCode && forecastData"
            class="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col"
          >
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
 class="w-5 h-5 text-blue-600">
    <path
        d="M21 21H4.6C4.03995 21 3.75992 21 3.54601 20.891C3.35785 20.7951 3.20487 20.6422 3.10899 20.454C3 20.2401 3 19.9601 3 19.4V3M20 8L16.0811 12.1827C15.9326 12.3412 15.8584 12.4204 15.7688 12.4614C15.6897 12.4976 15.6026 12.5125 15.516 12.5047C15.4179 12.4958 15.3215 12.4458 15.1287 12.3457L11.8713 10.6543C11.6785 10.5542 11.5821 10.5042 11.484 10.4953C11.3974 10.4875 11.3103 10.5024 11.2312 10.5386C11.1416 10.5796 11.0674 10.6588 10.9189 10.8173L7 15"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
    />
</svg>
                  </div>

                  <h2 class="text-lg font-semibold text-gray-900">
                    {{ $t('LineChart.chart.title') }}
                  </h2>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="flex items-center space-x-2">
                    <div class="w-3 h-3 bg-red-500 rounded-full" />

                    <span class="text-sm text-gray-600">{{ $t('LineChart.chart.legend.tenderRate') }}</span>
                  </div>

                  <div class="flex items-center space-x-2">
                    <div class="w-3 h-3 bg-blue-500 rounded-full" />

                    <span class="text-sm text-gray-600">{{ $t('LineChart.chart.legend.dailyRate') }}</span>
                  </div>

                  <div class="flex items-center space-x-2">
                    <div class="w-3 h-3 bg-green-500 rounded-full" />

                    <span class="text-sm text-gray-600">{{ $t('LineChart.chart.legend.toComplete') }}</span>
                  </div>

                  <div class="flex items-center space-x-2">
                    <div class="w-3 h-3 bg-orange-500 rounded-full" />

                    <span class="text-sm text-gray-600">{{ $t('LineChart.chart.legend.toDate') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex-1 w-full relative p-4">
              <Line
                :data="chartData"
                :options="chartOptions"
                :chart-id="chartId"
                :dataset-id-key="datasetIdKey"
                :plugins="plugins"
                :css-classes="cssClasses"
                :styles="styles"
                :width="width"
                :height="height"
              />
            </div>
          </div>

          <!-- Loading/Empty State for Chart -->
          <!-- This may be needed when wbs codes are loaded on demand, rather than at start -->
          <!-- <div
            v-else
            class="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col items-center justify-center p-8 transition-opacity duration-500 ease-in-out"
          >
            <div class="text-center">
              <svg
                class="w-16 h-16 text-gray-400 mx-auto mb-4"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 21H4.6C4.03995 21 3.75992 21 3.54601 20.891C3.35785 20.7951 3.20487 20.6422 3.10899 20.454C3 20.2401 3 19.9601 3 19.4V3M20 8L16.0811 12.1827C15.9326 12.3412 15.8584 12.4204 15.7688 12.4614C15.6897 12.4976 15.6026 12.5125 15.516 12.5047C15.4179 12.4958 15.3215 12.4458 15.1287 12.3457L11.8713 10.6543C11.6785 10.5542 11.5821 10.5042 11.484 10.4953C11.3974 10.4875 11.3103 10.5024 11.2312 10.5386C11.1416 10.5796 11.0674 10.6588 10.9189 10.8173L7 15"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <p class="text-lg font-semibold text-gray-900 mb-2">
                {{ !wbsCode ? 'Select a WBS Code' : 'Loading Chart Data...' }}
              </p>

              <p class="text-sm text-gray-600">
                {{ !wbsCode ? 'Choose a WBS code and date range to view production rate trends.' : 'Please wait while we load the forecast data.' }}
              </p>
            </div>
          </div> -->
        </div>
      </template>

      <div
        v-else
        class="flex flex-col justify-center items-center h-64 element-island"
      >
        <svg
    viewBox="0 0 24 24"
    fill="none"
 class="w-16 h-16 text-gray-400 mx-auto mb-4">
    <path
        d="M8 5.00005C7.01165 5.00082 6.49359 5.01338 6.09202 5.21799C5.71569 5.40973 5.40973 5.71569 5.21799 6.09202C5 6.51984 5 7.07989 5 8.2V17.8C5 18.9201 5 19.4802 5.21799 19.908C5.40973 20.2843 5.71569 20.5903 6.09202 20.782C6.51984 21 7.07989 21 8.2 21H15.8C16.9201 21 17.4802 21 17.908 20.782C18.2843 20.5903 18.5903 20.2843 18.782 19.908C19 19.4802 19 18.9201 19 17.8V8.2C19 7.07989 19 6.51984 18.782 6.09202C18.5903 5.71569 18.2843 5.40973 17.908 5.21799C17.5064 5.01338 16.9884 5.00082 16 5.00005M8 5.00005V7H16V5.00005M8 5.00005V4.70711C8 4.25435 8.17986 3.82014 8.5 3.5C8.82014 3.17986 9.25435 3 9.70711 3H14.2929C14.7456 3 15.1799 3.17986 15.5 3.5C15.8201 3.82014 16 4.25435 16 4.70711V5.00005"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
    />
</svg>

        <p class="text-lg font-semibold text-gray-900 mb-2">
          {{ $t('LineChart.emptyState.noWbsCodesFound') }}
        </p>

        <p class="text-sm text-gray-600">
          {{ $t('LineChart.emptyState.addWbsCodesMessage') }}
        </p>

        <RouterLink
          v-if="userStore.isRole(['project_admin', 'company_admin', 'engineer'])"
          :to="{ name: 'ProjectSettings', hash: '#self_perform' }"
          class="btn btn--primary-blue flex items-center space-x-2 mt-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
</svg>

          <span>{{ $t('LineChart.button.addWbsCodes') }}</span>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { useProjectTypeRedirect } from '@/composables/useProjectTypeRedirect';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { roundDecimals, safeDenominator, timezoneOffset } from '@/helpers';
import { tcc } from '@/helpers';
import { t } from '@/i18n';
import { useDailyQuantitiesStore } from '@/store/dailyQuantities';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useWbsCodesStore } from '@/store/wbsCodes';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { computed, reactive, ref, watch } from 'vue';
import { Line } from 'vue-chartjs';

const props = defineProps<{
  projectId: string;
}>();

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const projectsStore = useProjectsStore();
const wbsCodesStore = useWbsCodesStore();

const project = computed(() => {
  return projectsStore.project;
});

const today = new Date();

const firstDay = new Date(
  new Date(
    today.getFullYear(),
    today.getMonth(),
    1,
  ).getTime() - (timezoneOffset * 60 * 1000),
);

const lastDay = new Date(
  new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
  ).getTime() - (timezoneOffset * 60 * 1000),
);

const forecastForm = useFormData({
  wbs_code_id: undefined,
  start: firstDay.toISOString().slice(0, 10),
  end: lastDay.toISOString().slice(0, 10),
});

watch(() => props.projectId, (newValue) => {
  if(newValue) {
    wbsCodesStore.index(newValue).then((codes) => {
      forecastForm.form.wbs_code_id = codes[0]?.id;
    });
  }
}, { immediate: true });

const wbsCodes = computed(() => {
  return wbsCodesStore.models.where('project_id', project.value?.id).get();
});

const wbsCode = computed(() => {
  return wbsCodesStore.models.find(forecastForm.form.wbs_code_id);
});

const tender = reactive({
  earned_amount: 0,
  cost_to_date: 0,
  cost_to_complete: 0,
  cost_forecast: 0,
  profit: 0,
  to_complete_unit_cost: 0,
  to_date_unit_cost: 0,
});

const averageToDateRate = ref(0);
const forecastData = ref<forecast | undefined>(undefined);
// Chart configuration
const chartId = 'production-rate-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

watch(forecastData, (newValue) => {
  if(newValue) {
    tender.cost_forecast = newValue.cost_forecast;
    tender.cost_to_complete = newValue.cost_to_complete;
    tender.cost_to_date = newValue.cost_to_date;
    tender.earned_amount = newValue.earned_amount;
    tender.profit = newValue.profit;
    tender.to_complete_unit_cost = newValue.to_complete_unit_cost;
    tender.to_date_unit_cost = newValue.to_date_unit_cost;
    averageToDateRate.value = newValue.cost_to_date / safeDenominator(newValue.quantity_to_date);
  }
}, { immediate: true });

const chartData = computed(() => {
  if(!forecastData.value) {
    return {
      labels: [],
      datasets: [
        {
          label: t('LineChart.chart.legend.tenderRate'),
          data: [],
          fill: false,
          borderColor: 'red',
          tension: 0.1,
        },
        {
          label: t('LineChart.chart.legend.dailyRate'),
          data: [],
          fill: false,
          borderColor: 'blue',
          tension: 0.1,
        },
        {
          label: t('LineChart.chart.legend.toComplete'),
          data: [],
          fill: false,
          borderColor: 'green',
          tension: 0.1,
        },
        {
          label: t('LineChart.chart.legend.toDate'),
          data: [],
          fill: false,
          borderColor: 'orange',
          tension: 0.1,
        },
      ],
    };
  }

  const start = forecastForm.form.start ?
    new Date(forecastForm.form.start) :
    new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000);

  const end = forecastForm.form.end ? new Date(forecastForm.form.end) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  const tenderRates = Array.from({ length: diffDays }, () => forecastData.value.tender_unit_cost);
  const costToComplete = Array.from({ length: diffDays }, () => forecastData.value.to_complete_unit_cost);

  const labels = Array.from({ length: diffDays }, (_, i) => {
    const date = new Date(start.getTime() + (i * 24 * 60 * 60 * 1000));

    return date.toISOString().slice(0, 10);
  });

  const rates = [];

  labels.forEach((label) => {
    if(
      label in forecastData.value.range_quantities &&
      label in forecastData.value.range_cost
    ) {
      rates.push(forecastData.value.range_cost[label] / forecastData.value.range_quantities[label]);
    } else {
      rates.push(0);
    }
  });

  const averagesToDate = Array.from({ length: diffDays }, () => averageToDateRate.value);

  return {
    labels,
    datasets: [
      {
        label: t('LineChart.chart.legend.tenderRate'),
        data: tenderRates,
        fill: false,
        borderColor: 'red',
        tension: 0.1,
      },
      {
        label: t('LineChart.chart.legend.dailyRate'),
        data: rates,
        fill: false,
        borderColor: 'blue',
        tension: 0.1,
      },
      {
        label: t('LineChart.chart.legend.toComplete'),
        data: costToComplete,
        fill: false,
        borderColor: 'green',
        tension: 0.1,
      },
      {
        label: t('LineChart.chart.legend.toDate'),
        data: averagesToDate,
        fill: false,
        borderColor: 'orange',
        tension: 0.1,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },

    title: {
      display: false,
    },

    tooltip: {
      callbacks: {
        label(context: any) {
          const value = roundDecimals(context.parsed.y, 2);

          return context.dataset.label + ': ' + tcc(value) +
            (wbsCode.value?.unit ? `/${wbsCode.value.unit}` : '');
        },
      },
    },
  },

  maintainAspectRatio: false,
  scales: {
    y: {
      ticks: {
        callback(value: any) {
          return tcc(value) + (wbsCode.value?.unit ? `/${wbsCode.value.unit}` : '');
        },
      },
    },
  },
}));

const dailyQuantitiesStore = useDailyQuantitiesStore();
const fetchForecastAction = useStoreApiAction(dailyQuantitiesStore.forecast);

interface forecast {
  tender_cost: number;
  tender_quantity: number;
  cost_to_date: number;
  quantity_to_date: number;
  earned_amount: number;
  cost_to_complete: number;
  cost_forecast: number;
  profit: number;
  range_hours: [K: number];
  range_cost: [K: number];
  range_quantities: [K: number];
  tender_unit_cost: number;
  to_date_unit_cost: number;
  to_complete_unit_cost: number;
}

const fetchForecast = () => {
  forecastForm.resetErrors();

  fetchForecastAction.request(props.projectId, forecastForm.form).then((forecast: forecast) => {
    forecastData.value = forecast;
  }).catch((error) => {
    forecastForm.setErrors(error.data);
  });
};

watch(forecastForm.form, (newValue) => {
  if(
    newValue.wbs_code_id &&
    newValue.end &&
    newValue.start
  ) {
    fetchForecast();
  }
}, { immediate: true });

useProjectTypeRedirect();
const userStore = useUserStore();
</script>

<style scoped></style>
